<template>
  <div v-if="user" class="relative inline-flex z-50">
    <button ref="trigger" class="inline-flex justify-center items-center group" aria-haspopup="true"
            @click.prevent="toggleDropdown" :aria-expanded="dropdownOpen">
      <div class="flex items-center justify-center">
        <img v-if="$page.props.jetstream.managesProfilePhotos" :src="user.profile_photo_url"
             :alt="`${user.last_name} ${user.name} profile picture`" class="rounded-full w-12 h-12 object-cover"/>
        <span v-else class="inline-flex rounded-md">
          <Profile/>
          <button type="button"
                  class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:text-gray-700 focus:outline-none focus:bg-gray-50 active:bg-gray-50 transition ease-in-out duration-150">
              {{ $page.props.auth.user.name }}
              <svg class="ms-2 -me-0.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                   fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                   stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
          </button>
        </span>
      </div>

      <div class="flex items-center truncate">
        <svg class="h-2 aspect-square shrink-0 ml-2 fill-current text-black transition-transform"
             :class="{ 'rotate-180': dropdownOpen }" viewBox="0 0 12 12" v-if="!hideDropdown">
          <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z"/>
        </svg>
      </div>
    </button>
    <transition enter-active-class="transition ease-out duration-200 transform"
                enter-from-class="opacity-0 -translate-y-2" enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-out duration-200" leave-from-class="opacity-100"
                leave-to-class="opacity-0">
      <div v-show="dropdownOpen"
           class="z-[9999] origin-top-right absolute top-full w-80 bg-white border border-grey-50 p-5 rounded-xl shadow-lg mt-1 divide-zinc-100 divide-y overflow-visible"
           :class="align === 'right' ? 'right-0' : 'left-0'">
        <div class="flex flex-row gap-2 mt-1 mb-2">
          <div class="">
            <div class="text-md font-bold text-gray-800">{{ user.last_name }} {{ user.name }}</div>
            <div v-if="user.current_team_role" class="text-xs text-gray-500">{{ user.current_team_role.name }}</div>
            <div class="text-xs font-serif font-semibold text-gray-700 mt-1">{{ user.current_team.name }}</div>

            <!-- Période d'essai -->
            <div v-if="showTrialPeriod" class="mt-2 text-xs font-medium p-2 rounded"
                 :class="[trialColorClass, daysRemaining === 0 ? 'bg-teal-50' : '']">
              {{ trialMessage }}
              <div v-if="daysRemaining === 0" class="mt-1 text-xs">
                <Link :href="route('billing.show')" class="underline font-semibold">
                  Voir les détails de facturation
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div ref="dropdown" @focusin="dropdownOpen = true" @focusout="dropdownOpen = false"
             class="divide-grey-200 divide-y z-50">
          <slot name="links">

            <Link class="w-full font-medium text-sm text-gray-700 hover:text-green-200 flex items-center pt-3 mb-3"
                  :href="route('profile.show')" @click="dropdownOpen = false" as="button">Mon Profil
            </Link>

            <!-- Abonnement LCB-FT (VIG-450) : sans pastille « Nouveau » après la période de lancement (NG_MesDossiers).
                 Visible de tous les membres de l'office, en lecture seule hors Notaire Administrateur (R42).
                 `subscription` ne vaut null que tant que l'offre n'est pas lancée. -->
            <Link v-if="$page.props.subscription"
                  class="w-full font-medium text-sm text-gray-700 hover:text-green-200 flex items-center gap-2 pt-3 mb-3"
                  :href="route('subscription.show')" @click="dropdownOpen = false" as="button">
              Mon abonnement
            </Link>

            <!-- Team Settings -->
            <Link class="w-full font-medium text-sm text-gray-700 hover:text-green-200 flex items-center pt-3 mb-3"
                  :href="route('teams.show', $page.props.auth.user.current_team)" @click="dropdownOpen = false"
                  as="button">Mon compte VigIActe
            </Link>

            <!-- Billing -->
            <Link class="w-full font-medium text-sm text-gray-700 hover:text-green-200 flex items-center pt-3 mb-3"
                  :href="route('billing.show')" @click="dropdownOpen = false"
                  as="button">Facturation
            </Link>

            <!-- Stats -->
            <Link class="w-full font-medium text-sm text-gray-700 hover:text-green-200 flex items-center pt-3 mb-3"
                  :href="route('statistics.show')" @click="dropdownOpen = false"
                  as="button">Statistiques
            </Link>

            <!-- API & Webhooks -->
            <Link v-if="$page.props.canManageWebhooks"
                  class="w-full font-medium text-sm text-gray-700 hover:text-green-200 flex items-center pt-3 mb-3"
                  :href="route('webhook-endpoints.index')" @click="dropdownOpen = false"
                  as="button">API & Webhooks
            </Link>

            <DropdownLink v-if="$page.props.jetstream.hasApiFeatures" :href="route('api-tokens.index')">
              API Tokens
            </DropdownLink>

            <!-- Team Switcher -->
            <template v-if="hasMultipleTeams">
              <div class="border-t border-gray-200 mt-3 pt-3 mb-3">
                <div class="font-medium text-sm text-gray-500 mb-2">Changer d'étude</div>
                <template v-for="teamItem in allTeams" :key="teamItem.id">
                  <button
                      type="button"
                      class="w-full font-medium text-sm text-gray-600 hover:text-green-100 flex items-start py-2"
                      @click="switchToTeam(teamItem); dropdownOpen = false"
                  >
                    <svg v-if="teamItem.id === currentTeamId"
                         class="me-2 h-4 w-4 text-green-200 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none"
                         viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round"
                            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span v-else class="me-2 h-4 w-4 flex-shrink-0"></span>
                    <div class="text-left">
                      <div class="truncate font-serif text-xs font-semibold">{{ teamItem.name }}</div>
                      <div v-if="teamItem.code_crpcen" class="text-xs text-gray-400 font-normal">{{ teamItem.code_crpcen }}</div>
                    </div>
                  </button>
                </template>
              </div>
            </template>

            <Link class="w-full font-medium text-sm text-gray-700 hover:text-green-200 flex items-center pt-3"
                  :href="route('logout')" method="post" @click="dropdownOpen = false" as="button">Déconnexion
            </Link>
          </slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import Profile from '@/Components/Client/Icons/Profile.vue';
import {Link, router, usePage} from '@inertiajs/vue3';
import {computed, onMounted, onUnmounted, ref} from 'vue';
import DropdownLink from "@/Components/DropdownLink.vue";

defineProps(['align', 'hideDropdown']);

const switchToTeam = (team) => {
  router.put(route('current-team.update'), {
    team_id: team.id,
  }, {
    preserveState: false,
  });
};

const allTeams = computed(() => usePage().props?.auth?.user?.all_teams || []);
const currentTeamId = computed(() => usePage().props?.auth?.user?.current_team_id);
const hasMultipleTeams = computed(() => allTeams.value.length > 1);

defineEmits({
  click: null,
});

const dropdownOpen = ref(false)
const trigger = ref(null)
const dropdown = ref(null)

const user = computed(() => usePage().props?.auth?.user)
const team = computed(() => usePage().props?.team)

// Calcul de la période d'essai
const TRIAL_DAYS = 7; // 7 jours
const DISPLAY_AFTER_TRIAL_DAYS = 3; // Nombre de jours où le message "essai terminé" s'affiche

const daysFromCreation = computed(() => {
  if (!team.value || !team.value.created_at) return 0;

  const createdAt = new Date(team.value.created_at);
  const today = new Date();
  const diffTime = today - createdAt;
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
});

const daysRemaining = computed(() => {
  if (!team.value || !team.value.created_at) return 0;

  const createdAt = new Date(team.value.created_at);
  const trialEndDate = new Date(createdAt);
  trialEndDate.setDate(trialEndDate.getDate() + TRIAL_DAYS);

  const today = new Date();
  const diffTime = trialEndDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays > 0 ? diffDays : 0;
});

const daysAfterTrialEnd = computed(() => {
  if (daysRemaining.value > 0) return 0;

  return daysFromCreation.value - TRIAL_DAYS;
});

const showTrialPeriod = computed(() => {
  if (!team.value || !team.value.created_at) return false;

  // Nouvel office (essai de 14 jours et 5 dossiers, ou SIREN ayant déjà eu l'essai) : l'essai
  // est affiché par le bandeau du haut, l'ancien essai d'une semaine ne le concerne pas (VIG-450).
  const subscription = usePage().props?.subscription;
  if (subscription?.trial || subscription?.isNewClient) return false;

  // Afficher si toujours en essai
  if (daysRemaining.value > 0) return true;

  // Ou si l'essai est terminé depuis moins de DISPLAY_AFTER_TRIAL_DAYS jours
  return daysAfterTrialEnd.value <= DISPLAY_AFTER_TRIAL_DAYS;
});

const trialMessage = computed(() => {
  const trialEndDate = new Date(new Date(team.value.created_at));
  trialEndDate.setDate(trialEndDate.getDate() + TRIAL_DAYS);

  const formattedDate = trialEndDate.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  if (daysRemaining.value === 0) {
    return `Votre période d'essai est terminée. La facturation a débuté le ${formattedDate}`;
  } else if (daysRemaining.value === 1) {
    return "⚠️ Dernier jour de votre période d'essai !";
  } else if (daysRemaining.value <= 2) {
    return `⚠️ Plus que ${daysRemaining.value} jours d'essai. La facturation débutera le ${formattedDate}`;
  } else {
    return `${daysRemaining.value} jours d'essai restants`;
  }
});
const trialColorClass = computed(() => {
  if (daysRemaining.value === 0) {
    return "text-teal-600";
  } else if (daysRemaining.value <= 2) {
    return "text-orange-500";
  } else {
    return "text-blue-600";
  }
});

// close on click outside
const clickHandler = ({target}) => {
  if (!dropdownOpen.value || dropdown.value.contains(target) || trigger.value.contains(target)) return
  dropdownOpen.value = false
}
// close if the esc key is pressed
const keyHandler = ({keyCode}) => {
  if (!dropdownOpen.value || keyCode !== 27) return
  dropdownOpen.value = false
}
const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value
}
onMounted(() => {
  document.addEventListener('click', clickHandler)
  document.addEventListener('keydown', keyHandler)
})
onUnmounted(() => {
  document.removeEventListener('click', clickHandler)
  document.removeEventListener('keydown', keyHandler)
})


</script>
